/**
 * p80-read-more: folds a description to `data-lines` lines ending in "… Read more"
 * below `data-breakpoint` px (Figma M - PDP). The cut is computed by binary search on
 * the plain text so the button sits inline at the end of the last line; expanding
 * restores the original markup and shows "Read less".
 */
class P80ReadMore extends HTMLElement {
    /** @type {string} */
    #html = '';
    #expanded = false;
    /** @type {MediaQueryList | null} */
    #query = null;
    /** @type {ResizeObserver | null} */
    #observer = null;
    #width = 0;

    connectedCallback() {
        this.#html = this.innerHTML;
        const breakpoint = Number(this.dataset.breakpoint || 750);
        this.#query = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        this.#query.addEventListener('change', this.#render);
        this.#observer = new ResizeObserver((entries) => {
            const width = Math.round(entries[0]?.contentRect.width ?? 0);
            if (width !== this.#width) {
                this.#width = width;
                this.#render();
            }
        });
        this.#observer.observe(this);
        this.#render();
    }

    disconnectedCallback() {
        this.#query?.removeEventListener('change', this.#render);
        this.#observer?.disconnect();
    }

    #render = () => {
        if (!this.#query?.matches) {
            this.innerHTML = this.#html;
            return;
        }
        if (this.#expanded) {
            this.innerHTML = this.#html;
            this.append(' ', this.#button(this.dataset.less || 'Read less', true));
            return;
        }
        this.#fold();
    };

    #fold() {
        const lines = Number(this.dataset.lines || 3);
        this.innerHTML = this.#html;
        const lineHeight = parseFloat(getComputedStyle(this).lineHeight) || 20;
        const max = lineHeight * lines + 1;
        if (this.scrollHeight <= max) return;

        const blocks = [...this.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6')];
        const raw = blocks.length ? blocks.map((el) => el.textContent).join(' ') : this.textContent;
        const text = (raw || '').replace(/\s+/g, ' ').trim();
        const p = document.createElement('p');
        const span = document.createElement('span');
        p.append(span, '… ', this.#button(this.dataset.more || 'Read more', false));
        this.replaceChildren(p);

        let low = 0;
        let high = text.length;
        while (low < high) {
            const mid = Math.ceil((low + high) / 2);
            span.textContent = text.slice(0, mid).trimEnd();
            if (this.scrollHeight <= max) low = mid;
            else high = mid - 1;
        }
        let cut = text.slice(0, low);
        const space = cut.lastIndexOf(' ');
        if (space > 0 && low < text.length) cut = cut.slice(0, space);
        span.textContent = cut.replace(/[\s.,;:]+$/, '');
    }

    /**
     * @param {string} label
     * @param {boolean} expanded
     */
    #button(label, expanded) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'p80-read-more__toggle';
        button.textContent = label;
        button.setAttribute('aria-expanded', String(expanded));
        button.addEventListener('click', () => {
            this.#expanded = !expanded;
            this.#render();
            this.querySelector('.p80-read-more__toggle')?.focus();
        });
        return button;
    }
}

if (!customElements.get('p80-read-more')) {
    customElements.define('p80-read-more', P80ReadMore);
}
